import axios from "axios";
import { toast } from "react-toastify";

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}`, // Base URL from environment variable
  withCredentials: true, // Include credentials with requests
});

let lastReportTime = 0; // Tracks the last report timestamp
const REPORT_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

// Add a request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    const tenantId = sessionStorage.getItem("X-TenantID");

    if (jwtToken) {
      config.headers.Authorization = jwtToken; // Add JWT token to headers
    }
    if (tenantId) {
      config.headers["X-TenantID"] = tenantId; // Add X-TenantId to headers
    }

    config.headers["X-ProductID"] = process.env.REACT_APP_PRODUCT_ID; // Add ProductId to headers

    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const currentPath = window.location.pathname;

      if (error.response.status === 401) {
        // Handle token expiration by refreshing the token
        try {
          const refreshResponse = await axiosInstance.post(
            `${process.env.REACT_APP_URL}/refresh-token`,
            {},
            { withCredentials: true }
          );

          const newJwtToken = refreshResponse.headers["authorization"];
          const tenantId = sessionStorage.getItem("tenantId"); // Fetch Tenant ID from sessionStorage

          if (newJwtToken) {
            sessionStorage.setItem("jwtToken", newJwtToken);
            console.log("Updated JWT token from refresh response headers.");
          }

          const originalRequest = error.config;
          originalRequest.headers.Authorization = newJwtToken;

          if (tenantId) {
            originalRequest.headers["X-TenantId"] = tenantId; // Add X-TenantId to headers
          }

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          sessionStorage.removeItem("jwtToken");
          sessionStorage.removeItem("tenantId"); // Correct key name
          return Promise.reject(refreshError);
        }
      } else if (error.response.status === 403) {
        // Clear cookies
        document.cookie.split(";").forEach((cookie) => {
          document.cookie = cookie
            .replace(/^ +/, "")
            .replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`);
        });
        sessionStorage.removeItem("jwtToken"); // Redirect to login page
        window.location.href = "/login";
      } else if (error.response.status === 500) {
        const currentTime = Date.now();

        if (currentTime - lastReportTime >= REPORT_INTERVAL) {
          lastReportTime = currentTime;

          toast.error("Something went wrong. Sending a report...");

          const reportDetails = {
            title: "Server Error: 500",
            description: JSON.stringify({
              message: error.response.data?.message || "Unknown error",
              status: error.response.status,
              url: error.config.url,
              method: error.config.method,
              data: error.config.data,
              headers: error.config.headers,
            }),
          };

          try {
            await axiosInstance.post(`/bugs-report`, reportDetails);
            console.log("Error report sent successfully.");
          } catch (reportError) {
            console.error("Failed to send error report:", reportError);
          }
        } else {
          console.log(
            `Skipping error report. Next report allowed after ${Math.ceil(
              (REPORT_INTERVAL - (currentTime - lastReportTime)) / 1000
            )} seconds.`
          );
        }
      } else if (
        error.response.status === 501 &&
        currentPath !== "/error-500"
      ) {
        window.location.href = "/error-500";
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
